import React, { lazy } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Bed, Bath, Square, ArrowRight, MapPin, TreePine, Home as HomeIcon, Droplets, ShieldCheck, Sun, Zap, Fence, ShoppingBag, Cpu, Sparkles } from 'lucide-react';
import { houses, lands, Property } from '../data/properties';
function StatusBadge({
  status
}: {
  status: Property['status'];
}) {
  const map = {
    Selling: 'bg-emerald-500 text-white',
    'Sold Out': 'bg-ink text-white',
    'Sale Closed': 'bg-ink/70 text-white'
  } as const;
  return <span className={`text-[10px] font-bold tracking-[0.2em] uppercase px-3 py-1.5 rounded-full ${map[status]}`} data-id="element-120">
      {status === 'Selling' && <span className="inline-block w-1.5 h-1.5 rounded-full bg-white mr-2 animate-pulse" data-id="element-121" />}
      {status}
    </span>;
}
function PropertyCard({
  prop
}: {
  prop: Property;
}) {
  const isLand = prop.category === 'land';
  const link = `/${isLand ? 'land' : 'houses'}/${prop.slug}`;
  return <Link to={link} className="group relative bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-500 flex flex-col h-full" data-id="element-122">
      <div className="relative h-64 overflow-hidden bg-ink/5 shrink-0" data-id="element-123">
        <img src={prop.hero} alt={prop.name} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" data-id="element-124" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink/40 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" data-id="element-125" />
        <div className="absolute top-4 left-4" data-id="element-126">
          <StatusBadge status={prop.status} data-id="element-127" />
        </div>
        <div className="absolute top-4 right-4 bg-white/95 backdrop-blur-sm text-ink text-[10px] font-bold tracking-widest uppercase px-3 py-1.5 rounded-full flex items-center gap-1.5" data-id="element-128">
          {isLand ? <TreePine className="w-3 h-3" data-id="element-129" /> : <HomeIcon className="w-3 h-3" data-id="element-130" />}
          {prop.type || (isLand ? 'Land' : 'House')}
        </div>
      </div>

      <div className="p-7 flex flex-col flex-grow" data-id="element-131">
        <div className="flex items-center gap-1.5 text-xs font-semibold tracking-wider uppercase text-gold mb-3" data-id="element-132">
          <MapPin className="w-3 h-3 shrink-0" data-id="element-133" />
          <span className="truncate" data-id="element-134">{prop.location}</span>
        </div>
        <h3 className="text-2xl font-serif text-ink mb-3 group-hover:text-gold-dark transition-colors" data-id="element-135">
          {prop.name}
        </h3>
        <p className="text-ink/60 text-sm mb-6 flex-grow leading-relaxed line-clamp-2" data-id="element-136">
          {prop.intro}
        </p>

        {!isLand && prop.beds && <div className="flex items-center gap-4 text-sm text-ink/70 mb-6 pb-6 border-b border-ink/5 shrink-0" data-id="element-137">
            <span className="flex items-center gap-1.5" data-id="element-138">
              <Bed className="w-4 h-4" data-id="element-139" /> {prop.beds}
            </span>
            <span className="flex items-center gap-1.5" data-id="element-140">
              <Bath className="w-4 h-4" data-id="element-141" /> {prop.baths}
            </span>
            <span className="flex items-center gap-1.5" data-id="element-142">
              <Square className="w-4 h-4" data-id="element-143" /> {prop.sqft}
            </span>
          </div>}

        {isLand && prop.plotSizes && <div className="flex items-center gap-4 text-sm text-ink/70 mb-6 pb-6 border-b border-ink/5 shrink-0" data-id="element-144">
            <span className="flex items-center gap-1.5" data-id="element-145">
              <Square className="w-4 h-4" data-id="element-146" /> {prop.plotSizes.join(' · ')}
            </span>
          </div>}

        <div className="flex items-center justify-between mt-auto shrink-0" data-id="element-147">
          <span className="text-xl font-medium text-ink" data-id="element-148">
            {prop.priceFrom || 'Enquire'}
          </span>
          <span className="text-sm font-medium text-gold flex items-center gap-1 group-hover:gap-2 transition-all" data-id="element-149">
            View details <ArrowRight className="w-4 h-4" data-id="element-150" />
          </span>
        </div>
      </div>

      <div className="absolute bottom-0 left-0 h-1 bg-gold w-0 group-hover:w-full transition-all duration-500 ease-out" data-id="element-151" />
    </Link>;
}
const promoFeatures = [{
  icon: Droplets,
  label: 'Drainage'
}, {
  icon: ShieldCheck,
  label: 'Security'
}, {
  icon: Sun,
  label: 'Clean Energy'
}, {
  icon: Zap,
  label: '24/7 Electricity'
}, {
  icon: Fence,
  label: 'Perimeter Fence'
}, {
  icon: ShoppingBag,
  label: 'Estate Mall'
}, {
  icon: Cpu,
  label: 'Smart Home Auto'
}, {
  icon: Sparkles,
  label: 'Premium Finish'
}];
function Project2000Card() {
  return <motion.div initial={{
    opacity: 0,
    y: 30
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true
  }} transition={{
    duration: 0.6
  }} className="relative bg-ink rounded-2xl overflow-hidden flex flex-col h-full text-white shadow-lg" data-id="element-152">
      {/* Decorative glow */}
      <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-gold/20 blur-3xl pointer-events-none" data-id="element-153" />
      <div className="absolute -bottom-32 -left-20 w-72 h-72 rounded-full bg-gold/10 blur-3xl pointer-events-none" data-id="element-154" />

      {/* Header eyebrow */}
      <div className="relative px-7 pt-7" data-id="element-155">
        <div className="inline-flex items-center gap-1.5 text-[10px] font-bold tracking-[0.25em] uppercase text-gold bg-gold/10 border border-gold/20 px-3 py-1.5 rounded-full" data-id="element-156">
          <Sparkles className="w-3 h-3" data-id="element-157" />
          The Vision
        </div>
      </div>

      {/* Big headline */}
      <div className="relative px-7 pt-5 pb-6" data-id="element-158">
        <h3 className="font-serif leading-[0.95] tracking-tight" data-id="element-159">
          <span className="block text-[64px] sm:text-[72px] font-medium text-gold" data-id="element-160">
            2,000
          </span>
          <span className="block text-2xl sm:text-3xl text-white mt-1" data-id="element-161">
            Homes in 5 Years
          </span>
        </h3>
      </div>

      {/* Feature icon grid */}
      <div className="relative px-7 grid grid-cols-4 gap-2.5 mb-6" data-id="element-162">
        {promoFeatures.map((f, i) => <motion.div key={f.label} initial={{
        opacity: 0,
        y: 8
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        delay: 0.1 + i * 0.04
      }} className="flex flex-col items-center text-center gap-1.5 p-2 rounded-xl bg-white/5 border border-white/10 hover:border-gold/40 hover:bg-white/10 transition-colors" data-id="element-163">
            <f.icon className="w-4 h-4 text-gold shrink-0" data-id="element-164" />
            <span className="text-[9px] font-semibold leading-tight text-white/80" data-id="element-165">
              {f.label}
            </span>
          </motion.div>)}
      </div>

      {/* Mission card */}
      <div className="relative mt-auto px-5 pb-5" data-id="element-166">
        <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-5" data-id="element-167">
          <p className="text-[13px] leading-relaxed text-white/75" data-id="element-168">
            We're on a bold journey to deliver{' '}
            <span className="text-gold font-semibold" data-id="element-169">2,000 quality homes</span>{' '}
            within 5 years, redefining what it means to own property across
            Nigeria. It's about building communities, creating affordable
            ownership opportunities, and shaping the future of sustainable
            living.
          </p>
          <Link to="/who-we-are" className="inline-flex items-center gap-1.5 mt-4 text-[11px] font-bold tracking-[0.2em] uppercase text-gold hover:gap-2.5 transition-all" data-id="element-170">
            Our Mission <ArrowRight className="w-3.5 h-3.5" data-id="element-171" />
          </Link>
        </div>
      </div>
    </motion.div>;
}
export function FeaturedProperties() {
  const sellingHouses = houses.filter(h => h.status === 'Selling').slice(0, 3);
  const sellingLands = lands.filter(l => l.status === 'Selling').slice(0, 3);
  // If fewer than 3 selling houses, the promo card fills the remaining slot
  const showPromo = sellingHouses.length < 3;
  return <section id="properties" className="py-24 px-6 md:px-12 max-w-[1280px] mx-auto bg-bone scroll-mt-24 space-y-24" data-id="element-172">
      {/* Houses Section */}
      {sellingHouses.length > 0 && <div data-id="element-173">
          <div className="flex flex-col md:flex-row justify-between items-end mb-10 gap-6" data-id="element-174">
            <div data-id="element-175">
              <motion.div initial={{
            opacity: 0,
            y: 10
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} className="text-xs font-semibold tracking-[0.2em] uppercase text-gold mb-4" data-id="element-176">
                Featured Homes
              </motion.div>
              <motion.h2 initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} className="text-4xl md:text-5xl font-serif text-ink tracking-tight mb-4" data-id="element-177">
                Homes currently for sale
              </motion.h2>
              <motion.p initial={{
            opacity: 0
          }} whileInView={{
            opacity: 1
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.2
          }} className="text-ink/60 max-w-md" data-id="element-178">
                Explore our portfolio of premium developments designed to
                elevate your lifestyle.
              </motion.p>
            </div>
            <motion.div initial={{
          opacity: 0
        }} whileInView={{
          opacity: 1
        }} viewport={{
          once: true
        }} data-id="element-179">
              <Link to="/houses" className="text-ink font-medium border-b border-ink/20 pb-1 hover:border-gold hover:text-gold transition-colors" data-id="element-180">
                View all homes →
              </Link>
            </motion.div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-181">
            {sellingHouses.map((prop, i) => <motion.div key={prop.id} initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: i * 0.1,
          duration: 0.5
        }} className="h-full" data-id="element-182">
                <PropertyCard prop={prop} data-id="element-183" />
              </motion.div>)}
            {showPromo && <Project2000Card data-id="element-184" />}
          </div>
        </div>}

      {/* Land Section */}
      {sellingLands.length > 0 && <div data-id="element-185">
          <div className="flex flex-col md:flex-row justify-between items-end mb-10 gap-6" data-id="element-186">
            <div data-id="element-187">
              <motion.div initial={{
            opacity: 0,
            y: 10
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} className="text-xs font-semibold tracking-[0.2em] uppercase text-gold mb-4" data-id="element-188">
                Featured Land
              </motion.div>
              <motion.h2 initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} className="text-4xl md:text-5xl font-serif text-ink tracking-tight mb-4" data-id="element-189">
                Prime plots available
              </motion.h2>
              <motion.p initial={{
            opacity: 0
          }} whileInView={{
            opacity: 1
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.2
          }} className="text-ink/60 max-w-md" data-id="element-190">
                Secure your future with our strategically located land
                investments.
              </motion.p>
            </div>
            <motion.div initial={{
          opacity: 0
        }} whileInView={{
          opacity: 1
        }} viewport={{
          once: true
        }} data-id="element-191">
              <Link to="/land" className="text-ink font-medium border-b border-ink/20 pb-1 hover:border-gold hover:text-gold transition-colors" data-id="element-192">
                View all land →
              </Link>
            </motion.div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-193">
            {sellingLands.map((prop, i) => <motion.div key={prop.id} initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: i * 0.1,
          duration: 0.5
        }} className="h-full" data-id="element-194">
                <PropertyCard prop={prop} data-id="element-195" />
              </motion.div>)}
          </div>
        </div>}
    </section>;
}